import "@/lib/polyfills/promiseWithResolvers";
import { useWindowSize } from "@/hooks";
import { LocalStorageKey, playerObject } from "@/services/constants";
import { getLayout } from "@/services/services";
import "@/styles/globals.css";
import { Group, MantineProvider, Stack, Text } from "@mantine/core";
import { useClickOutside } from "@mantine/hooks";
import {
  ContextModalProps,
  ModalsProvider,
  closeAllModals,
} from "@mantine/modals";
import { NotificationsProvider } from "@mantine/notifications";
import type { AppProps } from "next/app";
import dynamic from "next/dynamic";
import Link from "next/link";
import { appWithTranslation } from "next-i18next";
import useTrans from "@/hooks/useTrans";
import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import * as gtag from "../lib/ga";
import Script from "next/script";

// const GA_ID = process.env.Measurement_ID;
// const GA_ID_test = gtag.fetch_GA_ID();

const Player = dynamic(
  () => import("@lottiefiles/react-lottie-player").then((m) => m.Player),
  { ssr: false },
);

function App({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const trans = useTrans();
  const ref = useClickOutside(closeAllModals);
  const [measurementId, setMeasurementId] = useState<string | null>(null);
  const [appFont, setAppFont] = useState("Nunito");
  // const GA_ID = process.env.NEXT_PUBLIC_Measurement_ID;

  useEffect(() => {
    console.log(`Build - ${process.env.NEXT_PUBLIC_BUILD_VERSION}`);
    console.log(`url ${process.env.NEXT_PUBLIC_API_URL}`);
    console.log(`basePath ${process.env.NEXT_PUBLIC_BASE_PATH}`);
  }, []);
  // gtag.fetch_GA_ID().then((id) => {
  //   console.log(`Measurement ID: ${id}`);
  //   setMeasurementId(id);
  // });

  useEffect(() => {
    console.log(`GA ID: ${measurementId}`);
  }, [measurementId]);

  useEffect(() => {
    const handleRouteChange = (url) => {
      gtag.pageview(url);
    };
    getLayout().then((layoutFromApi) => {
      if (layoutFromApi) {
        if (layoutFromApi.layout && layoutFromApi.layout.config) {
          if (layoutFromApi.layout.config.appFont)
            setAppFont(layoutFromApi.layout.config.appFont);
          setMeasurementId(layoutFromApi.layout.config.measurementId || null);
          if (layoutFromApi.layout.config.formDynamicStylePreset) {
            document.body.classList.add(
              layoutFromApi.layout.config.formDynamicStylePreset,
            );
          }
        }
      }
    });

    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.events]);

  const windowDimension = useWindowSize();
  const divider =
    windowDimension.width > 1440 ? 1 : 1440 / windowDimension.width;

  const successModal = ({
    context,
    id,
    innerProps,
  }: ContextModalProps<{
    text: string;
    content: string;
    code: string;
    color: string;
  }>) => (
    <>
      <Group noWrap ref={ref} h={400} position="center">
        {windowDimension.width > 500 ? (
          <Player
            style={{ width: 400 / divider }}
            src={playerObject}
            autoplay
            keepLastFrame={true}
          />
        ) : (
          <></>
        )}

        <Stack spacing={20}>
          <Stack spacing={10}>
            <Text
              fw={700}
              fz={windowDimension.width > 1300 ? 26 : 20}
              c={innerProps.color}
            >
              {innerProps.text}
            </Text>
            {/* {windowDimension.width > 1200 ? (
              <Text fz={20 / divider}>{innerProps.content}</Text>
            ) : (
              <></>
            )} */}
          </Stack>

          {innerProps.code != null || innerProps.code != undefined ? (
            <Stack spacing={10}>
              <Text fz={20}>{trans.modalApplyNow.searchCode}</Text>
              <Text fz={30} fw={700} c={innerProps.color} align="center">
                {innerProps.code}
              </Text>
              {windowDimension.width > 1200 ? (
                <Text fz={20 / divider} color={"red"}>
                  {trans.modalApplyNow.searchCodeBody}
                </Text>
              ) : (
                <></>
              )}

              <Group spacing={5}>
                <Text fz={20}>{trans.modalApplyNow.research}</Text>
                <Link href={"/research"} target={"_blank"}>
                  <Text fz={20} c={innerProps.color}>
                    {trans.modalApplyNow.here}
                  </Text>
                </Link>
                <Text fz={20}>{trans.modalApplyNow.researchBody}</Text>
              </Group>
            </Stack>
          ) : (
            <></>
          )}
        </Stack>
      </Group>
    </>
  );
  return (
    <>
      <style jsx global>{`
        :root {
          --app-font: ${appFont};
        }
      `}</style>

      <MantineProvider
        theme={{
          fontFamily: appFont,
          headings: { fontFamily: appFont },
          globalStyles: () => ({ body: { margin: "0 auto" } }),
        }}
        withGlobalStyles
        withNormalizeCSS
      >
        <ModalsProvider
          modalProps={{ size: "75%", radius: 32, withCloseButton: false }}
          modals={{ success: successModal }}
        >
          <NotificationsProvider position="top-right" zIndex={2077}>
            <Script
              strategy="afterInteractive"
              src={`https://www.googletagmanager.com/gtag/js?id=${measurementId}`}
            />
            <Script
              id="gtag-init"
              strategy="afterInteractive"
              dangerouslySetInnerHTML={{
                __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${measurementId}', {
              page_path: window.location.pathname,
            });
          `,
              }}
            />
            <Component {...pageProps} />
          </NotificationsProvider>
        </ModalsProvider>
      </MantineProvider>
    </>
  );
}

export default appWithTranslation(App);
